import { FC, useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import ControlledDateField from '../../../../../components/controlledComponents/ControlledDateField.component';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useStudentDetailsAttendanceHook } from '../../../../../../apis/students/studentDetails/useStudentDetailsService';
import PaginatedGrid from '../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CheckCircle, Cancel, Schedule, School } from '@mui/icons-material';
import { ATTENDANCE_STATUS } from '../../../../../../apis/__shared/attendance.interfaces';

interface StudentAttendanceTabProps {
  studentId: string;
}

const StudentAttendanceTab: FC<StudentAttendanceTabProps> = ({ studentId }) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const { data, isLoading, error } = useStudentDetailsAttendanceHook(
    studentId,
    {
      page,
      limit: pageSize,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
    }
  );

  const attendance = useMemo(() => data?.data || [], [data]);
  const totalCount = useMemo(() => data?.total ?? 0, [data]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case ATTENDANCE_STATUS.PRESENT:
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case ATTENDANCE_STATUS.ABSENT:
        return <Cancel sx={{ color: 'error.main' }} />;
      case ATTENDANCE_STATUS.LATE:
        return <Schedule sx={{ color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case ATTENDANCE_STATUS.PRESENT:
        return 'success';
      case ATTENDANCE_STATUS.ABSENT:
        return 'error';
      case ATTENDANCE_STATUS.LATE:
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'date',
        headerName: translateText('common.date'),
        width: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        },
      },
      {
        field: 'status',
        headerName: translateText('common.status'),
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          const status = params.value as string;
          return (
            <Box display="flex" alignItems="center" gap={1}>
              {getStatusIcon(status)}
              <Chip
                label={translateText(`students.studentDetails.attendance.status.${status.toLowerCase()}`)}
                size="small"
                color={getStatusColor(status)}
                variant="outlined"
              />
            </Box>
          );
        },
      },
      {
        field: 'period',
        headerName: translateText('students.studentDetails.attendance.period'),
        width: 150,
      },
    ],
    [translateText]
  );

  const rows = useMemo(() => {
    return attendance.map((record, index) => ({
      id: record.date || `attendance-${index}`,
      date: record.date,
      status: record.status,
      period: record.period,
    }));
  }, [attendance]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const summary = useMemo(() => {
    const present = attendance.filter((a) => a.status === ATTENDANCE_STATUS.PRESENT).length;
    const absent = attendance.filter((a) => a.status === ATTENDANCE_STATUS.ABSENT).length;
    const late = attendance.filter((a) => a.status === ATTENDANCE_STATUS.LATE).length;
    const total = attendance.length;
    const percentage = total > 0 ? ((present / total) * 100).toFixed(1) : '0';
    return { present, absent, late, total, percentage };
  }, [attendance]);

  if (!studentId) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-attendance-tab-container"
      >
        <Grid item xs={12}>
          <Alert severity="info" data-testid="student-attendance-tab-no-student">
            {translateText('students.studentDetails.attendance.noStudentId')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-attendance-tab-container"
      >
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
            data-testid="student-attendance-tab-loading"
          >
            <CircularProgress />
            <Typography
              variant="body2"
              sx={{ ml: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-attendance-tab-container"
      >
        <Grid item xs={12}>
          <Alert severity="error" data-testid="student-attendance-tab-error">
            {translateText('common.error')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={{ xs: 2, sm: 4 }}
      sx={{ py: { xs: 1, sm: 2 } }}
      data-testid="student-attendance-tab-container"
    >
      <Grid item xs={12}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
        >
          {translateText('students.studentDetails.attendance.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
            border: `1px solid ${theme.palette.success.light}40`,
            borderRadius: 2,
            transition: 'transform 0.2s, box-shadow 0.2s',
            height: '100%',
            py: 2,
            px: 6,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
            },
          }}
          data-testid="student-attendance-tab-card-present"
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box
                sx={{
                  color: theme.palette.success.main,
                  fontSize: { xs: 24, sm: 28, md: 32 },
                }}
              >
                <CheckCircle />
              </Box>
            </Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="body2"
              fontWeight={500}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {translateText('students.studentDetails.attendance.summary.present')}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
              color={theme.palette.success.main}
            >
              {summary.present}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.error.light}05 100%)`,
            border: `1px solid ${theme.palette.error.light}40`,
            borderRadius: 2,
            transition: 'transform 0.2s, box-shadow 0.2s',
            height: '100%',
            py: 2,
            px: 6,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${theme.palette.error.light}30`,
            },
          }}
          data-testid="student-attendance-tab-card-absent"
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box
                sx={{
                  color: theme.palette.error.main,
                  fontSize: { xs: 24, sm: 28, md: 32 },
                }}
              >
                <Cancel />
              </Box>
            </Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="body2"
              fontWeight={500}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {translateText('students.studentDetails.attendance.summary.absent')}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
              color={theme.palette.error.main}
            >
              {summary.absent}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
            border: `1px solid ${theme.palette.primary.light}40`,
            borderRadius: 2,
            transition: 'transform 0.2s, box-shadow 0.2s',
            height: '100%',
            py: 2,
            px: 6,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
            },
          }}
          data-testid="student-attendance-tab-card-percentage"
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: { xs: 24, sm: 28, md: 32 },
                }}
              >
                <School />
              </Box>
            </Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="body2"
              fontWeight={500}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {translateText('students.studentDetails.attendance.summary.percentage')}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
              color={theme.palette.primary.main}
            >
              {summary.percentage}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="student-attendance-tab-filters"
        >
          <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <ControlledDateField
              label={translateText('students.studentDetails.attendance.filters.fromDate')}
              id="from-date-filter"
              name="fromDate"
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(date: Dayjs | null) => setFromDate(date ? date.format('YYYY-MM-DD') : '')}
              isGridItem={false}
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <ControlledDateField
              label={translateText('students.studentDetails.attendance.filters.toDate')}
              id="to-date-filter"
              name="toDate"
              value={toDate ? dayjs(toDate) : null}
              onChange={(date: Dayjs | null) => setToDate(date ? date.format('YYYY-MM-DD') : '')}
              isGridItem={false}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="student-attendance-tab-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={page}
          currentPageSize={pageSize}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Grid>
    </Grid>
  );
};

export default StudentAttendanceTab;
